import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { BffService } from './BffService';
import { AuthUser, AuthMethod, UserType } from '../models/AuthUser';
import { SignalRService } from './SignalRService';

@Injectable({ providedIn: 'root' })
export class DualAuthService {
  private apiUrl = `${environment.backend.baseUrl}${environment.backend.apiUrl}`;
  private currentUserSubject = new BehaviorSubject<AuthUser | null>(null);
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private initPromise: Promise<void> | null = null;

  public currentUser$ = this.currentUserSubject.asObservable();
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(private http: HttpClient, private router: Router, private bff: BffService, private signalR: SignalRService) {
    this.initPromise = this.initializeAuthState();
  }

  public ensureInitialized(): Promise<void> {
    return (this.initPromise ??= this.initializeAuthState());
  }

  private async initializeAuthState(): Promise<void> {
    if (environment.authMode !== 'bff') return;
    try {
      // Small delay to allow browser to process Set-Cookie from OIDC redirect
      // Prevents race condition where /whoami is called before cookie is available
      await new Promise(resolve => setTimeout(resolve, 100));

      // First, determine session type without fetching claims
      const who: any = await firstValueFrom(
        this.http.get(`${this.apiUrl}/whoami`, { withCredentials: true })
      );

      if (!who?.authenticated) {
        this.currentUserSubject.next(null);
        this.isAuthenticatedSubject.next(false);
        return;
      }

      const userType = (who.userType || '').toString().toLowerCase();
      if (userType === 'employee') {
        // Do NOT call /me for employees. Build minimal profile from /whoami.
        const emailFromWho = (who?.profile?.email || who?.email || '').toString();
        const given = (who?.profile?.givenName || who?.givenName || '').toString();
        const family = (who?.profile?.familyName || who?.familyName || '').toString();
        const nameStr = (who?.profile?.name || who?.name || '').toString();
        let first = given;
        let last = family;
        if (!first && !last && nameStr) {
          const parts = nameStr.trim().split(/\s+/);
          if (parts.length >= 2) { first = parts[0]; last = parts[parts.length - 1]; }
          else if (parts.length === 1) { first = parts[0]; }
        }
        let user: AuthUser = {
          id: '',
          email: emailFromWho,
          firstName: first,
          lastName: last,
          userType: UserType.EMPLOYEE,
          authMethod: AuthMethod.MICROSOFT_SSO,
          isActive: true,
          roles: [],
          groups: []
        } as any;
        this.currentUserSubject.next(user);
        this.isAuthenticatedSubject.next(true);
        // Fetch minimal role info for UI navigation without hitting /me
        try {
          const roleInfo: any = await firstValueFrom(
            this.http.get(`${this.apiUrl}/whoami/roles`, { withCredentials: true })
          );
          const roles = Array.isArray(roleInfo?.roles) ? roleInfo.roles as string[] : [];
          const groups = Array.isArray(roleInfo?.groups) ? roleInfo.groups as string[] : [];
          try { console.log('[Auth] Employee roles loaded:', roles, 'groups:', groups); } catch { }
          user = { ...user, roles, groups } as any;
          this.currentUserSubject.next(user);
        } catch {
          // ignore; minimal employee session remains valid
        }
        return;
      }

      // Customer path: fetch /me once to populate claims for UI use
      const me: any = await firstValueFrom(
        this.http.get(`${this.apiUrl}/me`, { withCredentials: true })
      );
      const claims: Array<{ type: string; value: string }> = me?.claims || [];
      const roleTypes = new Set([
        'roles',
        'role',
        'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
      ]);
      const roles = claims.filter(c => roleTypes.has(c.type)).map(c => c.value);
      const groupTypes = new Set([
        'groups',
        'http://schemas.microsoft.com/claims/groups'
      ]);
      const groups = claims.filter(c => groupTypes.has(c.type)).map(c => c.value);
      const email = claims.find(c => c.type === 'emails')?.value || claims.find(c => c.type === 'preferred_username')?.value || '';

      // Try to fetch companyShortName from /User/me endpoint
      let companyShortName: string | undefined;
      try {
        const userMe: any = await firstValueFrom(
          this.http.get(`${this.apiUrl}/User/me`, { withCredentials: true })
        );
        companyShortName = userMe?.customer?.companyShortName;
      } catch {
        // Ignore - companyShortName is optional
      }

      const user: AuthUser = { id: '', email, firstName: me?.name || '', lastName: '', userType: UserType.CUSTOMER, authMethod: AuthMethod.MICROSOFT_SSO, isActive: true, roles, groups, companyShortName } as any;
      this.currentUserSubject.next(user);
      this.isAuthenticatedSubject.next(true);
    } catch {
      this.currentUserSubject.next(null);
      this.isAuthenticatedSubject.next(false);
    }
  }

  private setAuthenticatedState(isAuth: boolean, userType?: UserType, authMethod?: AuthMethod): void {
    this.isAuthenticatedSubject.next(isAuth);
    if (isAuth && userType && authMethod) {
      this.currentUserSubject.next({ id: '', email: '', firstName: '', lastName: '', userType, authMethod, isActive: true, roles: [] });
    } else {
      this.currentUserSubject.next(null);
    }
  }

  private clearAuthState(): void { this.setAuthenticatedState(false); }

  // Compatibility helpers used across the app
  isEmployee(): boolean { const u = this.currentUserSubject.getValue(); return !!u && (u.userType === UserType.EMPLOYEE || (u.roles || []).includes('Administrator')); }
  isCustomer(): boolean { const u = this.currentUserSubject.getValue(); return !!u && u.userType === UserType.CUSTOMER; }
  isAuthenticated(): boolean { return this.isAuthenticatedSubject.getValue(); }

  // BFF sign-ins
  loginEmployee(): Observable<any> {
    this.bff.signInEmployee();
    return of(null);
  }
  loginEmployeeRedirect(): void {
    this.bff.signInEmployee();
  }
  loginCustomerCiam(): Observable<any> {
    this.bff.signInCustomer();
    return of(null);
  }
  loginCustomerCiamRedirect(): void {
    this.bff.signInCustomer();
  }
  logout(): void {
    this.signalR.stopConnection();
    this.bff.signOut();
    this.clearAuthState();
    this.router.navigate(['/login']);
  }

  // Tokens not used in BFF mode
  getAccessToken(): Observable<string | null> { return of(null); }
  getAppAccessToken(): Observable<string> { return of(''); }
}
